import { useTranslation } from 'react-i18next'
import { ContractPageResponse } from '@/api/contract'

interface Props {
  data: ContractPageResponse
}

function formatAmount(amount: number | null): string {
  if (amount === null || amount === undefined) return '-'
  return amount.toLocaleString('ko-KR')
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-'
  try {
    return new Date(dateStr).toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' })
  } catch {
    return dateStr
  }
}

export default function ContractInfoSection({ data }: Props) {
  const { t } = useTranslation('contract')
  const remainingSeats = data.maxSeats - data.usedSeats
  const usageRate = data.maxSeats > 0 ? Math.round((data.usedSeats / data.maxSeats) * 100) : 0

  function paymentMethodLabel(method: string): string {
    switch (method) {
      case 'bank_transfer': return t('info.paymentMethodBankTransfer')
      case 'credit_card': return t('info.paymentMethodCreditCard')
      default: return method
    }
  }

  return (
    <div className="space-y-4">
      {/* 계약 정보 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">{t('info.title')}</h3>
        </div>
        <dl className="px-6 py-2">
          <div className="grid grid-cols-3 gap-4 py-3 border-b border-gray-100">
            <dt className="text-sm font-medium text-gray-500">{t('info.period')}</dt>
            <dd className="col-span-2 text-sm text-gray-900">
              {data.contractStartDate && data.contractEndDate
                ? `${data.contractStartDate} ~ ${data.contractEndDate}`
                : '-'}
            </dd>
          </div>
          <div className="grid grid-cols-3 gap-4 py-3 border-b border-gray-100">
            <dt className="text-sm font-medium text-gray-500">{t('info.maxSeats')}</dt>
            <dd className="col-span-2 text-sm text-gray-900">{t('info.seatsUnit', { count: data.maxSeats })}</dd>
          </div>
          <div className="grid grid-cols-3 gap-4 py-3 border-b border-gray-100">
            <dt className="text-sm font-medium text-gray-500">{t('info.usedSeats')}</dt>
            <dd className="col-span-2 text-sm text-gray-900">
              {t('info.seatsUnit', { count: data.usedSeats })}
              <span className="ml-2 text-gray-500">({t('info.usageRate', { rate: usageRate })})</span>
            </dd>
          </div>
          <div className="grid grid-cols-3 gap-4 py-3 border-b border-gray-100">
            <dt className="text-sm font-medium text-gray-500">{t('info.remainingSeats')}</dt>
            <dd className="col-span-2 text-sm text-gray-900">{t('info.seatsUnit', { count: remainingSeats })}</dd>
          </div>
          <div className="grid grid-cols-3 gap-4 py-3 border-b border-gray-100">
            <dt className="text-sm font-medium text-gray-500">{t('info.status')}</dt>
            <dd className="col-span-2">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                data.contractStatus === 'active'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {data.contractStatus === 'active' ? t('info.statusActive') : t('info.statusInactive')}
              </span>
            </dd>
          </div>
          <div className="grid grid-cols-3 gap-4 py-3 border-b border-gray-100">
            <dt className="text-sm font-medium text-gray-500">{t('info.amount')}</dt>
            <dd className="col-span-2 text-sm text-gray-900">{t('info.currencyUnit', { amount: formatAmount(data.contractAmount) })} <span className="text-gray-400">({t('info.vatExcluded')})</span></dd>
          </div>
          <div className="grid grid-cols-3 gap-4 py-3">
            <dt className="text-sm font-medium text-gray-500">{t('info.totalPaid')}</dt>
            <dd className="col-span-2 text-sm font-semibold text-gray-900">{t('info.currencyUnit', { amount: formatAmount(data.totalPaidAmount) })} <span className="text-gray-400 font-normal">({t('info.vatExcluded')})</span></dd>
          </div>
        </dl>
      </div>

      {/* 결제 내역 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">{t('info.paymentTitle')}</h3>
        </div>
        {data.payments.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('info.paymentDate')}</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('info.paymentAmount')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('info.paymentMethod')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('info.paymentNotes')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.payments.map((payment) => (
                  <tr key={payment.paymentId} className="hover:bg-gray-50">
                    <td className="px-6 py-3 text-sm text-gray-900">{formatDate(payment.paidAt)}</td>
                    <td className="px-6 py-3 text-sm text-gray-900 text-right">{t('info.currencyUnit', { amount: formatAmount(payment.amount) })}</td>
                    <td className="px-6 py-3 text-sm text-gray-600">{paymentMethodLabel(payment.paymentMethod)}</td>
                    <td className="px-6 py-3 text-sm text-gray-600">{payment.notes || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="px-6 py-8 text-center text-sm text-gray-400">{t('info.noPayments')}</div>
        )}
      </div>
    </div>
  )
}
